import React, { useEffect } from 'react';
import styled from 'styled-components';
import MinimalHeader from '../Components/MinimalHeader';
import shop from '../../Assets/how-it-works/shop.png';
import save from '../../Assets/how-it-works/save.png';
import safe from '../../Assets/how-it-works/safe.png';
import { Trans, useTranslation } from 'react-i18next';
import usePageTitle from '../../Common/Hooks/usePageTitle';
import { themeColor } from '../../Theming';
import HowItWorks, { HowItWorksItem } from '../Components/HowItWorks';
import {
  getBrowserCountry,
  getLocationCurrency,
} from '../../Common/Utils/locations';
import env from '../../env';
import {
  getRandomUserId,
  getUtmParamsForChromeExtension,
} from '../../Common/Utils/extensionSDK';
import { useFeatures } from '../../PluginFeatures';
import { sendPartnerFeatures } from '../../PluginFeatures/helpers';

const Container = styled.div`
  width: 100%;
  padding: 60px 120px;
`;

const Heading = styled.h1`
  text-align: center;
  margin: 60px 0 0;
  font-size: 2.5rem;
`;

const MoreQuestions = styled.p`
  margin: 40px auto;
  max-width: 645px;
  text-align: center;
  font-size: 1rem;
  line-height: 150%;
  & a {
    color: ${themeColor('darkestGrey')};
  }
`;

const howItWorksItems: HowItWorksItem[] = [
  { i18nKey: 'extension.howItWorks.brands', image: shop },
  { i18nKey: 'extension.howItWorks.easy', image: save },
  { i18nKey: 'extension.howItWorks.getStarted', image: safe },
];

export const sendExtensionInstalled = (utmParams: Record<string, unknown>) => {
  chrome.runtime
    .sendMessage(env.kindred_extension_id, {
      type: 'extensionInstalled',
      utmParams,
    })
    .catch(() => {
      console.log('Extension not ready or does not exist');
    });
};

const ExtensionInstalled: React.FC = () => {
  usePageTitle('Extension Installed');
  const { t } = useTranslation();
  const { autoActivation, loading } = useFeatures();

  useEffect(() => {
    const utmParams = getUtmParamsForChromeExtension();
    sendExtensionInstalled(utmParams);

    const browserCountry = getBrowserCountry();
    const randomUserId = getRandomUserId();

    if (typeof window.setKindredUser === 'function') {
      window.setKindredUser(env.kindred_extension_id, {
        id: randomUserId,
        currency: getLocationCurrency(browserCountry),
        countryCode: browserCountry,
      });
    }
  }, []);

  useEffect(() => {
    if (!loading) {
      sendPartnerFeatures(autoActivation);
    }
  }, [loading, autoActivation]);

  return (
    <Container>
      <MinimalHeader />
      <Heading>{t('extension.installed.title')}</Heading>
      <HowItWorks items={howItWorksItems} />
      <MoreQuestions>
        <Trans i18nKey="extension.installed.faq">
          Do you have any more questions? Feel free to visit our
          <a
            href={'https://sdk.kindred.co/FAQ'}
            target="_blank"
            rel="noopener noreferrer"
          >
            FAQs page
          </a>
          . Here, you will find most of the the answers about Kindred to help to
          get the most out of it.
        </Trans>
      </MoreQuestions>
    </Container>
  );
};

export default ExtensionInstalled;
